// Angular
import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

// WebApi
import {
  VoucherMessageViewDetailsVm as DetailsVm,
  VoucherMessageViewDto as MessageDto,
  VoucherMessageStatusEnum,
  VoucherMessageStatusAllEnum,
  VoucherApprovalStatusEnum,
  VoucherMessageChannelTypeEnum,
  VoucherMessageViewDto,
  EntityMetaForeignKeyDto,
} from "../../api";

// Syncfusion
import { ContextMenuOpenEventArgs, QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';

// Cerium
import { CerUiIcons } from '@ui/cer-ui-icons';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from '@ui/cer-ui-types';
import { FieldMetadata, NavigationCommandMetadata, NavigationCommandQuery, ViewMetadata } from 'src/cer/cer-data/cer-data-types';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { CustInvoiceView } from 'src/app/sales/cust-invoice.view';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { CerFormComponent } from '@form/cer-form.component';
import { CerFormPanesSettings, CerFormPaneVisible, uiCmdPaneRightToggle } from '@form/cer-form-panes/cer-form-panes.service';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { CerGridSelectionService } from 'src/cer/cer-grid/cer-grid-selection.service';

// App
import { CerAppChatService } from 'src/cer-app/cer-app-chat/cer-app-chat.service';

// Voucher
import { VoucherDataService, VoucherUpdateResult } from '../voucher-data.service';
import { VoucherHelper } from '../voucher.service';
import { VoucherFormatterValidationStatus } from '../voucher-list/voucher-formatter-validation-status';
import { uiCmdSeparator, uiCmdSeparatorHeader } from '@ui/cer-ui-commands';
import { CerFormViewSet } from '@form/cer-form-view-set';

@Injectable(
)

export class VoucherMessageViewService {
  // Commands 
  private uiCmdSyncMsg: UiCommand = { id: 'syncAll', text: 'Synkroniser', tooltipText: 'Synkroniser meddelelser fra indbakke (ALT+Y)', iconCss: CerUiIcons.research, target: '.e-header-content' };
  private uiCmdShowVoucherDetails: UiCommand = { id: 'showVoucherDetails', text: 'Vis godkendelse', tooltipText: ' godkendelse og kontering (ALT+X)', iconCss: CerUiIcons.editDetails, target: '.e-content' };
  private uiCmdSetStatusVoucher: UiCommand = { id: 'setStatusVoucher', text: "Sæt status 'Godkendelse'", iconCss: "e-menu-icon e-icons e-document-01", target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.Voucher } };
  private uiCmdSetStatusOnHold: UiCommand = { id: 'setStatusOnHold', text: "Sæt status 'Afventer'", iconCss: 'e-menu-icon e-icons e-document-01', target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.OnHold } };
  private uiCmdSetStatusStatement: UiCommand = { id: 'setStatusStatement', text: "Sæt status 'Kontoudtog'", iconCss: "e-menu-icon e-icons e-document-01", target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.AccountStatement } };
  private uiCmdSetStatusForwarded: UiCommand = { id: 'setStatusForwarded', text: "Sæt status 'Videresendt'", iconCss: "e-menu-icon e-icons e-document-01", target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.Forwarded } };
  private uiCmdSetStatusRejected: UiCommand = { id: 'setStatusRejected', text: "Sæt status 'Annulleret'", iconCss: "e-menu-icon e-icons e-close-01", target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.Rejected } };
  private uiCmdSetStatusInbox: UiCommand = { id: 'setStatusInbox', text: "Sæt status 'Indbakke'", iconCss: "e-menu-icon e-icons e-document-01", target: ".e-content", data: { messageStatus: VoucherMessageStatusEnum.Inbox } };

  private uiCmdVendorShow: UiCommand = { id: 'vendorShow', text: "Vis kreditor", iconCss: CerUiIcons.link, target: ".e-content" };

  // Ui commands
  private uiCmdDetailToggle = Object.assign(new Object(), uiCmdPaneRightToggle, { text: 'Vis vedhæftninger', tooltipText: 'Vis vedhæftninger i højre side [Ctrl]+[Pil højre]' });

  // View panes
  public paneSetupComplete$: Subject<CerFormPanesSettings> = new Subject<CerFormPanesSettings>();

  // View
  private viewMetadata: ViewMetadata = {
    name: 'beskeder', dataApiName: 'VoucherMessageView', dataApiParams: [],
    text: 'Beskeder', textSingular: 'Besked', titleFields: ['description', 'statusName'],
    allowEdit: false, allowDelete: false, allowCreate: false,
    primaryKey: 'id', baseFields: ['id'],
    onBeforeNavigations: (e => VoucherMessageViewService.navigationsGet(e))
  };

  public static navigationsGet(e: NavigationCommandQuery): NavigationCommandMetadata[] {
    const columnName = e.fieldMeta?.name;
    if (columnName?.length > 0) {
      switch (columnName) {
        case 'validationStatus':
          return [{ link: 'fk', title: 'Vis genkendelse', foreignKey: { parentEntity: 'FormRecognizerDocument', parentColumn: 'refRowId', column: 'id' }}];
        case 'fromEmail':
          return [{ link: 'fk', title: 'Vis afsender historik', foreignKey: { parentEntity: 'VoucherMessageMailVendorHistory', parentColumn: 'fromEmail', column: 'fromEmail' }}];
      }
    }
    return [];
  }

  private fieldMetadata: FieldMetadata[] = [
    { name: 'id', text: "Besked id", format: 'Integer', visible: false, width: '80px' },
    { name: 'status', text: "Status id", format: 'Integer', visible: false, width: '80px' },
    { name: 'validationStatus', text: "Genkendelsestatus", width: '20px', allowFiltering: false, allowSearching: false, foreignKeyTableName: 'VoucherValidationStatus', formatter: new VoucherFormatterValidationStatus() },
    { name: 'statusName', text: "Status besked", visible: false, width: '120px' },
    { name: 'channel', text: "Kanal id", format: 'Integer', visible: false, width: '80px' },
    { name: 'channelType', text: "Kanal type", visible: false, width: '80px' },
    { name: 'channelName', text: "Kanal", visible: false, width: '80px' },
    { name: 'recieved', text: "Modtaget", format: 'DateLong' },
    { name: 'description', text: "Beskrivelse", width: '200px' },
    { name: 'fromEmail', text: "Fra adresse", width: '150px' },
    { name: 'fromName', text: "Fra navn", width: '150px' },
    { name: 'toEmails', text: "Til adresse", visible: false, width: '150px' },
    { name: 'toNames', text: "Til navn", visible: false, width: '150px' },
    { name: 'ccEmails', text: "CC e-adresser", visible: false, width: '150px' },
    { name: 'ccNames', text: "CC navne", visible: false, width: '150px' },
    { name: 'attachmentCount', text: "Vedhæftninger", width: '100px', visible: false },
    { name: 'created', text: "Oprettet", format: 'DateLong', orderBy: 'Descending' },
    { name: 'createdByShortName', text: "Oprettet af", visible: false },
    { name: 'modified', text: "Ændret", format: 'DateLong', visible: false },
    { name: 'modifiedByShortName', text: "Ændret af", visible: false }
  ];
  private toolbarCommands: UiCommand[] = [this.uiCmdDetailToggle];
  private _contextMenuCommands: UiCommand[] = [
    this.uiCmdSyncMsg,
    uiCmdSeparatorHeader,
    this.uiCmdShowVoucherDetails,
    uiCmdSeparator,
    this.uiCmdSetStatusVoucher, this.uiCmdSetStatusOnHold, this.uiCmdSetStatusStatement,
    this.uiCmdSetStatusForwarded, this.uiCmdSetStatusRejected, this.uiCmdSetStatusInbox,
    uiCmdSeparator,
    this.uiCmdVendorShow,
    uiCmdSeparator
  ];
  public get contextMenuCommands(): UiCommand[] {
    return this._contextMenuCommands;
  }
  public set contextMenuCommands(value: UiCommand[]) {
    this._contextMenuCommands = value;
  }
  private keyboardShortcuts: UiKeyboardShortcut[] = [
    { code: 'ArrowRight', ctrl: true, alt: false, shift: false, cmd: this.uiCmdDetailToggle },
    { code: 'ArrowRight', ctrl: true, alt: false, shift: true, cmd: this.uiCmdDetailToggle },
    { code: 'ArrowLeft', ctrl: true, alt: false, shift: false, cmd: this.uiCmdDetailToggle },
    { code: 'ArrowLeft', ctrl: true, alt: false, shift: true, cmd: this.uiCmdDetailToggle },
    //  { code: 'ArrowDown', ctrl: true, alt: false, shift: false, cmd: this.uiCmdBottomToggle },
    //  { code: 'ArrowDown', ctrl: true, alt: false, shift: true, cmd: this.uiCmdBottomToggle },
    //  { code: 'ArrowUp', ctrl: true, alt: false, shift: false, cmd: this.uiCmdBottomClose },
    //  { code: 'ArrowUp', ctrl: true, alt: true, shift: true, cmd: this.uiCmdBottomClose }
    //  { code: 'KeyV', ctrl: false, alt: true, shift: false, cmd: this.uiCmdShowInfoPane, global: true },
    { code: 'KeyY', ctrl: false, alt: true, shift: false, cmd: this.uiCmdSyncMsg, global: true },
    { code: 'KeyO', ctrl: false, alt: true, shift: false, cmd: this.uiCmdShowVoucherDetails, global: true },
    { code: 'KeyX', ctrl: false, alt: true, shift: false, cmd: this.uiCmdShowVoucherDetails, global: true }
  ];

  // Grid
  constructor(
    private voucherDataService: VoucherDataService,
    private ui: CerPromptService,
    private chatService: CerAppChatService,
  ) {
  }

  public init() {
    this.manage(this.chatService.chats$.subscribe(chat => this.voucherDataService.detailsVmMapRefreshFromChat(chat)));
    this.manage(this.voucherDataService.setupComplete$.subscribe(ok => this.dataSetupReady()));
    this.manage(this.voucherDataService.detailsVm$.subscribe(vm => this.onDetailVm(vm)));

  }

  private subscriptionManager: Subscription = new Subscription();
  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  private form: CerFormComponent;
  public initForm(form: CerFormComponent) {
    this.form = form;
    this.formSetup();
  }

  private formView = new CerFormView();
  public formviewSet() {
    const view = new CerFormView();
    
    view.onCommandEvent = (e => this.onCommand(e));

    view.viewMetadata = this.viewMetadata;
    view.fieldMetadata = this.fieldMetadata;
    view.listSelectionMode = 'row';
    view.toolbarCommands = this.toolbarCommands;
    view.contextMenuCommands = this.contextMenuCommands;
    view.keyboardShortcuts = this.keyboardShortcuts;
    
    this.formView = view;
    
    const viewSet = new CerFormViewSet();
    viewSet.viewAdd(view);
    this.form.formContextService.viewSetShow(viewSet);

    if (this.voucherDataService.custInvoiceVisible) {
      this.custInvoiceViewAdd(view);
    }
  }

  public initGrid(grid: CerGridComponent) {
    grid.viewMetadata = this.viewMetadata;
    grid.fieldMetadata = this.fieldMetadata;
    grid.selectionMode = /*isExpenseVoucher ? 'checkboxMulti' :*/ 'row';
    grid.toolbarCommands = this.toolbarCommands;
    grid.contextMenuCommands = this.contextMenuCommands;
    grid.keyboardShortcuts = this.keyboardShortcuts;
    grid.commmand.subscribe(e => this.onCommand(e));
    
    if (this.voucherDataService.custInvoiceVisible) {
      const parentView = CerFormView.formViewByComponent(grid);
      this.custInvoiceViewAdd(parentView);
    }
  }

  private onDetailVm(vm: DetailsVm): void {
    if (vm?.voucherMessageViewDto) {
      var messageDto: MessageDto = {} as MessageDto;
      Object.assign(messageDto, vm.voucherMessageViewDto);
      if (messageDto?.id) {
        var id: number = messageDto.id;
        var selectedData: any = this.grid?.selection.rowDataById(id);
        if (selectedData) {
          messageDto.modified.setMilliseconds(0);
          messageDto.created.setMilliseconds(0);
          if (!CerGridSelectionService.rowDataIsEqual(selectedData, messageDto)) {
            if (messageDto.modified < selectedData.modified) {
              console.error("Modified date is older than existing data", messageDto.modified, selectedData.modified);
            }
            else {
              this.grid.selection.rowDataSetById(id, messageDto);
            }
          }
        }
      }
    }
  }

  public research() {
    this.grid.rowSelectedKeep();
    this.grid.research();
  }

  private researchUpdate(dataUpdate: VoucherUpdateResult) {
    if (dataUpdate.ok && dataUpdate.messageDtoOrig?.id) {
      var messageId: number = dataUpdate.messageDtoOrig.id;
      if (messageId > 0) {
        this.voucherDataService.detailsVmGetPromise(messageId, null).then(detailsVm => {
          this.research();
        });
      }
    }
  }

  private dataSetupIsReady: boolean = false;
  private dataSetupReady() {
    this.dataSetupIsReady = true;
    this.formSetup();
  }

  private formSetup() {
    if (!this.dataSetupIsReady || this.form == null) {
      return;
    }
    this.dataSetupIsReady = false;
    this.formSetupInit();
    this.formviewSet();
  }

  private formSetupInit() {
    if (this.voucherDataService.isExpenseVoucher) {
      this.setupUiTextForExpense();
    }

    // Filter CustInvoice
    if (this.voucherDataService.filterCustInvoice) {
      this.viewMetadata.dataApiParams = ['$CustInvoice'];
    }

    // Filter status
    if (this.voucherDataService.filterMessageStatus) {
      var statusField = this.fieldMetadata.find(f => f.name == 'status');
      if (statusField) {
        statusField.filterValue = this.voucherDataService.filterMessageStatus;
        statusField.filterOperator = 'equal';
      }
      this.contextMenuCommands.filter(cmd => cmd.data?.messageStatus == this.voucherDataService.filterMessageStatus)
        .forEach(cmd => cmd.visible = false);
    }
    else {
      var statusNameField = this.fieldMetadata.find(f => f.name == 'statusName');
      if (statusNameField) {
        statusNameField.visible = true;
      }
    }

    // Filter channel
    if (this.voucherDataService.filterChannelType) {
      var channelTypeField = this.fieldMetadata.find(f => f.name == 'channelType');
      if (channelTypeField) {
        channelTypeField.filterValue = this.voucherDataService.filterChannelType;
        channelTypeField.filterOperator = 'equal';
      }

      var isVendorVoucher: boolean = this.voucherDataService.filterChannelType == VoucherMessageChannelTypeEnum.VendorVoucher;
      if (isVendorVoucher) {
        this.contextMenuCommands = this.contextMenuCommands.filter(c => c.id != this.uiCmdSetStatusVoucher.id);
      }
      else {
        this.contextMenuCommands = this.contextMenuCommands.filter(c => c.id != this.uiCmdVendorShow.id);
      }

      // Show sync on toolbar
      if (this.voucherDataService.filterMessageStatus == VoucherMessageStatusAllEnum.Inbox) {
        this.toolbarCommands.unshift(this.uiCmdSyncMsg);
        if (!VoucherMessageViewService.messageStatusHarSupportDetailDialog(VoucherMessageStatusEnum.Inbox, this.voucherDataService.filterChannelType)) {
          this.contextMenuCommands = this.contextMenuCommands.filter(cmd => cmd.id != this.uiCmdShowVoucherDetails.id && cmd.id != uiCmdSeparator.id);
        }
        else if (isVendorVoucher) {
          this.contextMenuCommands = this.contextMenuCommands.filter(cmd => cmd.id != this.uiCmdSetStatusVoucher.id);
        }
      }
    }

    const custInvoiceVisible = this.voucherDataService.custInvoiceVisible;
    const bottomVis: CerFormPaneVisible = isVendorVoucher ? 'collapse' : custInvoiceVisible ? 'show' : 'hide';
    const panes: CerFormPanesSettings = {
      main: { visible: 'show', size: '60%', sizeSecondary: '60%', dynamicOverride: true },
      right: { visible: 'show', size: '40%' },
      bottom: { visible: bottomVis, size: '40%', dynamicOverride: true },
      orientationPrimary: 'horizontal'
    };
    this.paneSetupComplete$.next(panes);
  }

  private custInvoiceViewAdd(parentView: CerFormView) {
    const viewSetCustInvoice = CustInvoiceView.viewSetGet(parentView, { parentPrimaryKey: 'msgId', parentForeignKey: 'voucherMessage' }, 'bottom', 'bottom');
    this.form.formContextService.viewSetShow(viewSetCustInvoice);
  }

  private setupUiTextForExpense() {
    this.uiCmdShowVoucherDetails.text = "Rediger bilag";
    this.uiCmdSetStatusVoucher.text = "Sæt status 'Bilag'";
  }

  private grid: CerGridComponent;
  private onCommand(e: UiCommandEvent): void {
    if (e.appComponent != null && this.grid != e.appComponent && e.appComponent instanceof CerGridComponent) {
      if (e.appComponent.viewMetadata.dataApiName = 'VoucherMessageView') {
        this.grid = e.appComponent;
      }
    }

    if (e.commandId != null) {
      switch (e.commandId) {
        case this.uiCmdSyncMsg.id:
          this.voucherDataService.apiVoucherChannelSync().then(result => { if (result > 0) { this.research() } });
          break;
        case this.uiCmdShowVoucherDetails.id:
          var calledFromChat: boolean = e.columnName == 'chatCount';
          this.voucherDetailsOpen(this.dtoFromArgs(e.args), calledFromChat);
          break;
        case this.uiCmdVendorShow.id:
          this.vendorShow(e.rowData, e.appComponent);
          break;
      }
    }
    if (e.actionType == UiActionTypeEnum.ContextMenuOpen) {
      this.onContextMenuOpen(e.args);
    }
    if (e.actionType == UiActionTypeEnum.ContextMenuItem) {
      if (e.command?.data?.messageStatus) {
          this.setMessageStatus(e);
      }
    }
    if (e.actionType == UiActionTypeEnum.RowDoubleClick) {
      var dto: MessageDto = this.dtoFromArgs(e.args);
      if (dto?.id && VoucherMessageViewService.messageStatusHarSupportDetailDialog(dto.status, this.voucherDataService.filterChannelType)) {
        var calledFromChat: boolean = e.columnName == 'chatCount';
        this.voucherDetailsOpen(this.dtoFromArgs(e.args), calledFromChat);
      }
    }
    if (e.source == UiCommandSourceEnum.ActionComplete) {
      switch (e.actionType) {
        case UiActionTypeEnum.RowSelect:
          this.voucherDataService.detailsVmGetFromMap(this.dtoFromArgs(e.args)?.id);
          break;
      }
    }
  }

  private dtoFromArgs(args: any, useSelectedRow: boolean = true): MessageDto {
    var dto: MessageDto = args?.rowData;
    if (dto == null) {
      dto = args?.rowInfo?.rowData;
    }
    if (dto == null) {
      dto = args?.data;
    }
    if (dto == null) {
      dto = args?.rowData;
    }
    if (dto == null && args.args) {
      dto = this.dtoFromArgs(args.args, useSelectedRow);
    }
    if (dto == null && useSelectedRow) {
      dto = <MessageDto>this.grid.rowSelectedData();
      console.log("Command on row selection", dto?.id);
    }
    if (dto == null) {
      console.log("No row data");
    }
    return dto;
  }

  private voucherGridQueryCellInfoFromVmMap(args: QueryCellInfoEventArgs, detailsVmMap: Map<number, DetailsVm>) {
    if (args.column.field === 'chatCount') {
      var data: any = args.data;
      var detailsVm: DetailsVm = null;
      if (detailsVmMap.has(data.msgId)) {
        detailsVm = detailsVmMap.get(data.msgId);
      }
      this.voucherGridQueryCellInfoFromVm(args, detailsVm);
    }
  }

  private voucherGridQueryCellInfoFromVm(args: QueryCellInfoEventArgs, detailsVm: DetailsVm) {
    if (args.column.field === 'chatCount') {
      if (detailsVm && detailsVm.chatDtoList) {
        var n: number = detailsVm.chatDtoList.length;
        if (n > 0) {
          args.cell.classList.add('chat-active');
        }
        else {
          args.cell.classList.add('chat-none');
        }
      }
      else {
        args.cell.classList.add('chat-unknown');
      }
    }
  }

  // Grid context menu open - disable menu items
  private onContextMenuOpen(args: ContextMenuOpenEventArgs) {
    var messageDto: MessageDto = this.dtoFromArgs(args, false);
    var messageStatusFrom: VoucherMessageStatusEnum = messageDto?.status;
    this.grid.grid.contextMenuItems.forEach(item => {
      var id = (item as any).id;
      if (id?.length > 0) {
        if (id == this.uiCmdShowVoucherDetails.id) {
          var enabled: boolean = VoucherMessageViewService.messageStatusHarSupportDetailDialog(messageStatusFrom, this.voucherDataService.filterChannelType);
          //console.log("details" + " = " + enabled);
          this.grid.grid.contextMenuModule.contextMenu.enableItems([id], enabled, true);
        }
        else {
          var cmd: UiCommand = this.contextMenuCommands.find(cmd => cmd.id == id);
          if (cmd) {
            var messageStatusTo: VoucherMessageStatusEnum = (cmd?.data?.messageStatus);
            if (messageStatusTo != null) {
              var approvalStatusFrom: VoucherApprovalStatusEnum = null;
              var enabled: boolean = VoucherHelper.isMessageStatusChangeEnabled(messageStatusFrom, messageStatusTo, approvalStatusFrom);
              this.grid.grid.contextMenuModule.contextMenu.enableItems([id], enabled, true);
            }
          }
        }
      }
    });
    this.grid.grid.contextMenuModule.contextMenu
  }

  // Set message status
  private setMessageStatus(e: UiCommandEvent) {
    var messageDto: MessageDto = this.dtoFromArgs(e);
    var messageStatusTo: VoucherMessageStatusEnum = e.command?.data?.messageStatus;
    if (messageDto && messageStatusTo && messageDto.status != messageStatusTo) {
      var proformaStatusChange: boolean = messageStatusTo == VoucherMessageStatusEnum.Voucher &&
        VoucherMessageViewService.messageStatusHarSupportDetailDialog(messageDto.status, this.voucherDataService.filterChannelType);
      var dialogConfig: any = proformaStatusChange ? this.voucherDetailsDialogGetConfig(messageDto) : null;
      this.voucherDataService.apiSetMessageStatus(messageDto, messageStatusTo, proformaStatusChange, dialogConfig).then(
        result => this.researchUpdate(result)
      );
    }
  }

  // Show vendor
  private vendorShow(messageDto: VoucherMessageViewDto, parentComponent: ICerViewComponent) {
    if (messageDto?.id != null) {
      this.voucherDataService.detailsVmGetFromMapPromise(messageDto.id).then(
        vm => {
          const parentView = this.formView ?? CerFormView.formViewByComponent(parentComponent);
          this.form.formContextService.entityShow('Vendor', null, parentView, 'main', null, null, { entityName: vm.constructor.name, row: vm });
          this.form.formContextService.routeService.drawerClose(true);
        });
    }
  }

  // Open details form
  public voucherDetailsOpen(messageDto: MessageDto, calledFromChat: boolean = false) {
    if (messageDto && VoucherMessageViewService.messageStatusHarSupportDetailDialog(messageDto.status, this.voucherDataService.filterChannelType)) {
      var config: any = this.voucherDetailsDialogGetConfig(messageDto, calledFromChat);
      if (messageDto.status == VoucherMessageStatusEnum.Inbox) {
        this.voucherDataService.apiSetMessageStatus(messageDto, VoucherMessageStatusEnum.Voucher, true, config).then(
          result => this.researchUpdate(result)
        );
      }
      else {
        this.voucherDataService.voucherDetailsOpen(messageDto, config).then((result) => {
          this.researchUpdate(result);
        });
      }
    }
    else {
      this.ui.errorToast("Åbn besked ikke tilladt");
    }
  }

  public VoucherChannelSync() {
    this.voucherDataService.apiVoucherChannelSync().then(
      msgCount => { if (msgCount > 0) { this.research() } });
  }

  public voucherDetailsDialogAttachmentIdxDefault: number = 0;
  private voucherDetailsDialogGetConfig(messageDto: MessageDto, showChat: boolean = false): any {
    var attachmentsActive: boolean = (this.form.panesService.visible('right') || messageDto.status == VoucherMessageStatusEnum.Inbox || messageDto.status == VoucherMessageStatusEnum.Voucher);
    var calledFromVoucher: boolean = (messageDto.status == VoucherMessageStatusEnum.Voucher);
    var editPosting: boolean = !calledFromVoucher;
    var config: any = this.voucherDataService.voucherDetailsDialogGetConfig(messageDto, attachmentsActive, calledFromVoucher, editPosting, this.voucherDetailsDialogAttachmentIdxDefault, false, showChat);
    return config;
  }

  private static messageStatusHarSupportDetailDialog(messageStatusFrom: VoucherMessageStatusEnum, filterChannelType: VoucherMessageChannelTypeEnum): boolean {
    return (messageStatusFrom &&
      ((messageStatusFrom == VoucherMessageStatusEnum.Inbox && filterChannelType == VoucherMessageChannelTypeEnum.VendorVoucher) ||
        (messageStatusFrom == VoucherMessageStatusEnum.Voucher)));
  }
}
